/**
 * SubsystemSlos — SLOs numéricos + sparkline 24h por subsistema.
 *
 * Fontes (todas existentes, sem migration):
 *   - v19_health_checks: row_count + status por table_name (poll a cada ~10min)
 *   - multimodal_metrics_hourly: p50/p95 latency, ok/error rate (24h)
 *
 * Subsistemas: WM, LTM, Skill, Multimodal, Factual, People Memory.
 * Cada card mostra: nome, valor agregado 24h, status (ok/warn/critical),
 * último horário com dado, mini-sparkline com row_count por checagem.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ResponsiveContainer, AreaChart, Area, Tooltip as ReTooltip, XAxis, YAxis } from "recharts";
import { Brain, Database, Zap, Image as ImageIcon, Search, Users } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type Status = "ok" | "warn" | "critical";

interface Subsystem {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** v19_health_checks.table_name — pode ser múltiplos */
  sources: string[];
}

const SUBSYSTEMS: Subsystem[] = [
  {
    key: "wm",
    label: "Working Memory",
    description: "Estado curto da conversa (últimos turnos, open loops)",
    icon: Brain,
    sources: ["syra_working_memory"],
  },
  {
    key: "ltm",
    label: "Long-term Memory",
    description: "Memórias consolidadas + composição de prompt",
    icon: Database,
    sources: ["syra_prompt_composition_traces"],
  },
  {
    key: "skill",
    label: "Skills v19",
    description: "Execuções da camada de skills (matcher → executor)",
    icon: Zap,
    sources: ["syra_skill_executions_v19"],
  },
  {
    key: "multimodal",
    label: "Multimodal",
    description: "Mídia processada (imagem, áudio, vídeo, sticker)",
    icon: ImageIcon,
    sources: ["syra_media_items"],
  },
  {
    key: "factual",
    label: "Factual Search",
    description: "Buscas grounding em web/KB para reduzir alucinação",
    icon: Search,
    sources: ["syra_factual_search_runs"],
  },
  {
    key: "people",
    label: "People Memory",
    description: "Memória individual por participante",
    icon: Users,
    sources: ["syra_person_memories", "people_memory_acceptance"],
  },
];

interface CheckRow {
  table_name: string;
  status: Status;
  row_count: number | null;
  expected_min: number | null;
  checked_at: string;
  details: Record<string, unknown> | null;
}

function statusVariant(status: Status): "default" | "secondary" | "destructive" {
  if (status === "ok") return "default";
  if (status === "warn") return "secondary";
  return "destructive";
}

function statusColor(status: Status): string {
  if (status === "ok") return "text-emerald-500";
  if (status === "warn") return "text-amber-500";
  return "text-destructive";
}

function aggregate(rows: CheckRow[]): { total: number; latest: CheckRow | null; series: { t: string; v: number }[]; worst: Status } {
  if (rows.length === 0) return { total: 0, latest: null, series: [], worst: "critical" };
  const latest = rows[0];
  const total = rows.reduce((a, r) => a + (r.row_count ?? 0), 0);
  const series = [...rows]
    .reverse()
    .map((r) => ({ t: r.checked_at, v: r.row_count ?? 0 }));
  const worstRank: Record<Status, number> = { ok: 0, warn: 1, critical: 2 };
  const worst = rows.reduce<Status>((acc, r) => (worstRank[r.status] > worstRank[acc] ? r.status : acc), "ok");
  return { total, latest, series, worst };
}

function SubsystemCard({ subsystem, byTable }: { subsystem: Subsystem; byTable: Map<string, CheckRow[]> }) {
  const Icon = subsystem.icon;
  const allRows = subsystem.sources.flatMap((src) => byTable.get(src) ?? []);
  // Ordena por checked_at desc
  allRows.sort((a, b) => new Date(b.checked_at).getTime() - new Date(a.checked_at).getTime());
  const agg = aggregate(allRows);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={`w-5 h-5 shrink-0 ${statusColor(agg.worst)}`} />
            <CardTitle className="text-base truncate">{subsystem.label}</CardTitle>
          </div>
          <Badge variant={statusVariant(agg.worst)} className="capitalize shrink-0">
            {agg.worst}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{subsystem.description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{agg.total.toLocaleString("pt-BR")}</span>
          <span className="text-xs text-muted-foreground">linhas em 24h</span>
        </div>
        {agg.latest && (
          <div className="text-xs text-muted-foreground">
            Última checagem:{" "}
            {formatDistanceToNow(new Date(agg.latest.checked_at), { addSuffix: true, locale: ptBR })}
            {agg.latest.expected_min != null && (
              <span className="ml-1">· mínimo esperado: {agg.latest.expected_min}/h</span>
            )}
          </div>
        )}

        {agg.series.length > 0 ? (
          <div className="h-20 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={agg.series} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id={`g-${subsystem.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" hide />
                <YAxis hide />
                <ReTooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                  labelFormatter={(t) => new Date(String(t)).toLocaleString("pt-BR")}
                  formatter={(v: number) => [`${v} linhas`, "row_count"]}
                />
                <Area type="monotone" dataKey="v" stroke="hsl(var(--primary))" fill={`url(#g-${subsystem.key})`} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-20 flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded">
            Sem checagens nas últimas 24h
          </div>
        )}

        {agg.latest?.details && Object.keys(agg.latest.details).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Detalhes</summary>
            <pre className="mt-2 p-2 bg-muted rounded text-[10px] overflow-x-auto">
              {JSON.stringify(agg.latest.details, null, 2)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

interface MmHourly {
  hour: string;
  total: number;
  ok: number;
  errors: number;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
}

function MultimodalLatencyChart() {
  const { data, isLoading } = useQuery({
    queryKey: ["health", "mm-hourly-24h"],
    queryFn: async (): Promise<MmHourly[]> => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("multimodal_metrics_hourly")
        .select("hour, total, ok, errors, p50_latency_ms, p95_latency_ms")
        .gte("hour", since)
        .order("hour", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MmHourly[];
    },
    refetchInterval: 60_000,
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Sem métricas multimodais nas últimas 24h.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Latência multimodal (24h)</CardTitle>
        <p className="text-xs text-muted-foreground">p50 / p95 em ms por hora</p>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="g-p95" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-p50" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="hour"
                tickFormatter={(t) => new Date(String(t)).toLocaleTimeString("pt-BR", { hour: "2-digit" })}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <ReTooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                labelFormatter={(t) => new Date(String(t)).toLocaleString("pt-BR")}
              />
              <Area type="monotone" dataKey="p95_latency_ms" name="p95" stroke="hsl(var(--destructive))" fill="url(#g-p95)" strokeWidth={2} />
              <Area type="monotone" dataKey="p50_latency_ms" name="p50" stroke="hsl(var(--primary))" fill="url(#g-p50)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SubsystemSlos() {
  const { data: rows, isLoading } = useQuery({
    queryKey: ["health", "subsystem-slos-24h"],
    queryFn: async (): Promise<CheckRow[]> => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("v19_health_checks")
        .select("table_name, status, row_count, expected_min, checked_at, details")
        .gte("checked_at", since)
        .order("checked_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as CheckRow[];
    },
    refetchInterval: 60_000,
  });

  const byTable = new Map<string, CheckRow[]>();
  for (const r of rows ?? []) {
    const arr = byTable.get(r.table_name) ?? [];
    arr.push(r);
    byTable.set(r.table_name, arr);
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {SUBSYSTEMS.map((s) => (
          <Skeleton key={s.key} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {SUBSYSTEMS.map((s) => (
          <SubsystemCard key={s.key} subsystem={s} byTable={byTable} />
        ))}
      </div>
      <MultimodalLatencyChart />
    </div>
  );
}
